import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getAccessToken } from "@/services/authService";
import { SessionExpiredDialog } from "@/components/auth/SessionExpiredDialog";

interface AuthContextType {
  isAuthenticated: boolean;
  setAuthenticated: (auth: boolean) => void;
}

// WebView Auth Context
interface WebViewAuth {
  isNativeWebView: boolean;
  accessToken?: string;
  refreshToken?: string;
  deviceId?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const WebViewAuthContext = createContext<{
  webViewAuth: WebViewAuth;
  setWebViewAuth: React.Dispatch<React.SetStateAction<WebViewAuth>>;
} | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setAuthenticated] = useState<boolean>(!!getAccessToken());
  const [webViewAuth, setWebViewAuth] = useState<WebViewAuth>({ isNativeWebView: false });

  // Listen for changes in authentication (e.g., login/logout, cookie changes)
  useEffect(() => {
    const checkAuth = () => {
      setAuthenticated(!!getAccessToken());
    };
    // Listen for storage/cookie changes (cross-tab)
    window.addEventListener("storage", checkAuth);
    // Optionally, poll for cookie changes (js-cookie doesn't have events)
    const interval = setInterval(checkAuth, 1000);
    return () => {
      window.removeEventListener("storage", checkAuth);
      clearInterval(interval);
    };
  }, []);

  // Listen for postMessage from React Native WebView
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data && data.type === 'SET_TOKENS') {
          setWebViewAuth({
            isNativeWebView: true,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            deviceId: data.deviceId,
          });
        }
      } catch (e) {
        // Ignore non-JSON messages
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Determine authentication: webview tokens or web cookies
  const effectiveIsAuthenticated =
    (webViewAuth.isNativeWebView && !!webViewAuth.accessToken) || isAuthenticated;

  return (
    <AuthContext.Provider value={{ isAuthenticated: effectiveIsAuthenticated, setAuthenticated }}>
      <WebViewAuthContext.Provider value={{ webViewAuth, setWebViewAuth }}>
        <SessionExpiredDialog />
        {children}
      </WebViewAuthContext.Provider>
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}

export function useWebViewAuth() {
  const ctx = useContext(WebViewAuthContext);
  if (!ctx) throw new Error("useWebViewAuth must be used within an AuthProvider");
  return ctx;
} 